import React from "react";
import { useDispatchWrapper } from "../../../../hooks";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../../../../redux/messages/messagesActions";
import {
  sendBillingModeOtpAsync,
  switchBillingModeAsync,
  ISwitchBillingModeResponse,
} from "../../../../services/subcriptionApi";
import { launchRazorpay } from "../../../../utils/launchRazorpay";
import { ISubscriptionPlan } from "../../../../redux/subscription/subscription.types";
import { useAuth } from "../../../../contexts/AuthProvider/AuthProvider";
import {
  BillingMode,
  ChangeBillingStep,
} from "../changeBillingMode.types";
import {
  OTP_RESEND_SECONDS,
  OTP_VALID_MINUTES,
  getTargetBillingMode,
} from "../changeBillingMode.constants";

interface IUseChangeBillingModeArgs {
  open: boolean;
  companyOutletUuid: string;
  currentBillingMode: BillingMode;
  userUuid: string;
  plan: ISubscriptionPlan;
  onSwitched: () => void;
  onClose: () => void;
}

export const useChangeBillingMode = ({
  open,
  companyOutletUuid,
  currentBillingMode,
  userUuid,
  plan,
  onSwitched,
  onClose,
}: IUseChangeBillingModeArgs) => {
  const dispatch = useDispatchWrapper();
  const { user } = useAuth();

  const targetMode = getTargetBillingMode(currentBillingMode);

  const [step, setStep] = React.useState<ChangeBillingStep>("MODE");
  const [otp, setOtp] = React.useState("");
  const [otpError, setOtpError] = React.useState<string | null>(null);
  const [resendTimer, setResendTimer] = React.useState(0);
  const [loading, setLoading] = React.useState(false);

  // Reset when the dialog is (re)opened.
  React.useEffect(() => {
    if (open) {
      setStep("MODE");
      setOtp("");
      setOtpError(null);
      setResendTimer(0);
      setLoading(false);
    }
  }, [open]);

  // Countdown for resend.
  React.useEffect(() => {
    if (resendTimer <= 0) return;
    const id = window.setInterval(() => {
      setResendTimer((t) => (t <= 1 ? 0 : t - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [resendTimer]);

  const sendOtp = React.useCallback(async () => {
    setLoading(true);
    dispatch(saveLoaderProgress());
    await sendBillingModeOtpAsync(
      {
        otp_for: "BILLING_MODE_CHANGE",
        user_uuid: userUuid,
        email: "",
        mobile: user.mobile || "",
        channel: "WHATSAPP",
        valid_minutes: OTP_VALID_MINUTES,
      },
      (data, isSuccess) => {
        dispatch(saveLoaderCompleted());
        setLoading(false);
        if (isSuccess) {
          setStep("OTP");
          setOtp("");
          setOtpError(null);
          setResendTimer(OTP_RESEND_SECONDS);
          dispatch(
            showMessage({
              type: "success",
              message: data?.message || "OTP sent via WhatsApp.",
              displayAs: "snackbar",
            }),
          );
        } else {
          dispatch(
            showMessage({
              type: "error",
              message: data?.message || "Failed to send OTP.",
              displayAs: "snackbar",
            }),
          );
        }
      },
    );
  }, [dispatch, userUuid, user]);

  const verifyAndSwitch = React.useCallback(async () => {
    if (!otp || otp.length < 4) {
      setOtpError("Enter the OTP sent to you.");
      return;
    }
    setOtpError(null);
    setLoading(true);
    dispatch(saveLoaderProgress());

    await switchBillingModeAsync(
      {
        company_outlet_uuid: companyOutletUuid,
        billing_mode: targetMode,
        otp,
      },
      async (res: ISwitchBillingModeResponse | any, isSuccess) => {
        dispatch(saveLoaderCompleted());
        setLoading(false);

        if (!isSuccess) {
          const message = res?.message || "Failed to switch billing mode.";
          setOtpError(message);
          dispatch(
            showMessage({ type: "error", message, displayAs: "snackbar" }),
          );
          return;
        }

        dispatch(
          showMessage({
            type: "success",
            message: res?.message || "Billing mode updated.",
            displayAs: "snackbar",
          }),
        );

        const data = res?.data;
        // If a payment is required, drive the Razorpay flow.
        if (data && data.rzp_type && data.rzp_type !== "none") {
          await launchRazorpay({
            data,
            name: plan.plan_name,
            description: plan.description,
            prefill: { name: user.name, email: user.email, contact: "" },
            onSuccess: () => {
              onSwitched();
            },
            onError: (message) =>
              dispatch(
                showMessage({ type: "error", message, displayAs: "snackbar" }),
              ),
          });
        }

        onSwitched();
        onClose();
      },
    );
  }, [
    otp,
    dispatch,
    companyOutletUuid,
    targetMode,
    plan,
    user,
    onSwitched,
    onClose,
  ]);

  return {
    step,
    targetMode,
    currentBillingMode,
    otp,
    otpError,
    resendTimer,
    loading,
    setOtp,
    sendOtp,
    verifyAndSwitch,
    resend: sendOtp,
    goToMode: () => setStep("MODE"),
  };
};
